import * as THREE from 'three/webgpu';
import {
  Fn,
  uniform,
  time,
  vec2,
  vec3,
  float,
  mix,
  positionLocal,
  positionWorld,
  normalWorld,
  cameraPosition,
} from 'three/tsl';
import {
  DEFAULT_WORLD_THEME,
  type WorldThemeDefinition,
} from './worldTheme';

// Sun-tint color used by the specular highlight. Pulled out so the Fn() closure
// does not reallocate a Vector each setup.
const SUN_SPECULAR_TINT = vec3(1.0, 0.92, 0.8);

export class ProceduralOcean {
  private readonly mesh: THREE.Mesh;
  private readonly material: THREE.MeshPhysicalNodeMaterial;
  private readonly sunDirection = uniform(new THREE.Vector3(0.3, 0.9, 0.2));
  private readonly deepColor = uniform(new THREE.Color());
  private readonly shallowColor = uniform(new THREE.Color());
  private readonly causticColor = uniform(new THREE.Color());
  private readonly fogColor = uniform(new THREE.Color());
  private readonly shorelineDir = uniform(new THREE.Vector2(1, 0));

  constructor(
    scene: THREE.Scene,
    worldTheme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
  ) {
    const geometry = new THREE.PlaneGeometry(
      worldTheme.water.size,
      worldTheme.water.size,
      52,
      52,
    );
    geometry.rotateX(-Math.PI / 2);

    this.deepColor.value.set(worldTheme.water.deepColor);
    this.shallowColor.value.set(worldTheme.water.shallowColor);
    this.causticColor.value.set(worldTheme.water.causticColor);
    this.fogColor.value.set(worldTheme.sky.fogColor);
    const shorelineRad = THREE.MathUtils.degToRad(
      worldTheme.terrain.shorelineAngleDeg,
    );
    this.shorelineDir.value.set(Math.cos(shorelineRad), Math.sin(shorelineRad));

    // Capture locals so TSL Fn() closures don't `this`-capture the class.
    const sunDirection = this.sunDirection;
    const deepColor = this.deepColor;
    const shallowColor = this.shallowColor;
    const causticColor = this.causticColor;
    const fogColor = this.fogColor;
    const shorelineDir = this.shorelineDir;

    // Three-wave vertical displacement — matches the legacy GLSL coefficients.
    const wavePositionNode = Fn(() => {
      const waveA = positionLocal.x.mul(0.02).add(time.mul(0.35)).sin().mul(0.45);
      const waveB = positionLocal.x
        .add(positionLocal.z)
        .mul(0.014)
        .sub(time.mul(0.28))
        .sin()
        .mul(0.32);
      const waveC = positionLocal.z.mul(0.018).add(time.mul(0.22)).cos().mul(0.25);
      return positionLocal.add(vec3(0, waveA.add(waveB).add(waveC), 0));
    });

    // Deep/shallow base color tinted with a shoreline gradient, then fog-fogged
    // toward the sky color at the horizon.
    const waterColorNode = Fn(() => {
      const horizon = positionWorld.xz
        .normalize()
        .dot(shorelineDir)
        .mul(0.5)
        .add(0.5)
        .saturate();
      const base = mix(
        deepColor,
        shallowColor,
        float(0.35).add(horizon.mul(0.35)),
      );
      const dist = positionWorld.xz.sub(cameraPosition.xz).length();
      const fade = dist.smoothstep(260, 1200).mul(0.75);
      return mix(base, fogColor, fade);
    });

    // Sun specular glint + scrolling caustic shimmer — both bloom via the
    // emissive MRT target wired up by SceneManager's RenderPipeline.
    const waterEmissiveNode = Fn(() => {
      const sunDir = sunDirection.normalize();
      const sunSpec = normalWorld.dot(sunDir).max(0).pow(128);
      const sunTint = SUN_SPECULAR_TINT.mul(sunSpec).mul(0.9);

      const causticA = positionWorld.x.mul(0.12).add(time.mul(0.5)).sin();
      const causticB = positionWorld.z.mul(0.09).sub(time.mul(0.4)).sin();
      const caustic = causticA.mul(causticB).mul(0.5).add(0.5);
      const causticTint = causticColor.mul(caustic).mul(0.18);

      return sunTint.add(causticTint);
    });

    // Distance-based opacity so the ocean fades out near the camera and fills
    // in the horizon — matches the legacy silhouette.
    const waterOpacityNode = Fn(() => {
      const dist = positionWorld.xz.sub(cameraPosition.xz).length();
      return float(0.8).mul(dist.smoothstep(120, 450));
    });

    this.material = new THREE.MeshPhysicalNodeMaterial({
      transparent: true,
      depthWrite: false,
      side: THREE.DoubleSide,
    });
    this.material.positionNode = wavePositionNode();
    this.material.colorNode = waterColorNode();
    this.material.emissiveNode = waterEmissiveNode();
    this.material.opacityNode = waterOpacityNode();
    this.material.roughnessNode = float(0.05);
    this.material.metalnessNode = float(0.0);
    this.material.iorNode = float(1.33);
    this.material.transmissionNode = float(0.5);

    this.mesh = new THREE.Mesh(geometry, this.material);
    this.mesh.position.set(
      worldTheme.water.offsetX,
      worldTheme.water.level,
      worldTheme.water.offsetZ,
    );
    scene.add(this.mesh);
  }

  // `deltaMs` and `cameraPosition` are accepted for API compatibility with
  // ArenaRenderer; `time` and `cameraPosition` are TSL built-ins that update
  // automatically, so only the sun direction still needs a per-frame push.
  update(
    _deltaMs: number,
    sunDirection: THREE.Vector3,
    _cameraPosition: THREE.Vector3,
  ): void {
    this.sunDirection.value.copy(sunDirection);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
